import type { components } from "../../api/schema";

type BatchValidationGroup = components["schemas"]["BatchValidationGroup"];
type BatchWarningGroup = components["schemas"]["BatchWarningGroup"];
type DeclarationScope = components["schemas"]["ScopeType"];
type SupplierLayer = components["schemas"]["VariableSupplier"];

export const SUPPLIER_LABELS: Record<SupplierLayer, string> = {
  agent: "from Agent",
  persona: "from Persona",
  row: "from Scenario row",
  scenario: "from Scenario",
  sweep: "from launch override",
};

export const DECLARATION_LAYER_LABELS: Record<DeclarationScope, string> = {
  agent: "the Agent layer",
  persona: "the Persona layer",
  scenario: "the Scenario layer",
};

export const SUPPLIER_LAYER_LABELS: Record<SupplierLayer, string> = {
  agent: "the Agent layer",
  persona: "the Persona layer",
  scenario: "the Scenario layer",
  row: "the Scenario row layer",
  sweep: "the sweep override layer",
};

const LAYER_ORDER: Record<SupplierLayer, number> = {
  agent: 0,
  persona: 1,
  scenario: 2,
  row: 3,
  sweep: 4,
};

const CAUSE_PRECEDENCE: Record<BatchValidationGroup["cause"], number> = {
  required_missing: 0,
  missing: 1,
  non_scalar: 2,
  type_error: 3,
  not_allowed: 4,
};

function formatList(values: string[]): string {
  if (values.length < 2) {
    return values[0] ?? "";
  }
  if (values.length === 2) {
    return `${values[0] ?? ""} and ${values[1] ?? ""}`;
  }
  return `${values.slice(0, -1).join(", ")}, and ${values.at(-1) ?? ""}`;
}

function orderedLayers<T extends SupplierLayer>(layers: T[]): T[] {
  return [...new Set(layers)].sort(
    (left, right) => LAYER_ORDER[left] - LAYER_ORDER[right],
  );
}

function declarationLayers(group: BatchValidationGroup): string {
  const layers = formatList(
    orderedLayers(
      (group.declarations ?? []).map(({ scope_type }) => scope_type),
    ).map((scope) => DECLARATION_LAYER_LABELS[scope]),
  );
  return layers || "the declaring layers";
}

function supplierLayers(group: BatchValidationGroup): string {
  return formatList(
    orderedLayers(group.supplied_by ?? []).map(
      (supplier) => SUPPLIER_LAYER_LABELS[supplier],
    ),
  );
}

function cells(group: BatchValidationGroup): string {
  return `${String(group.cell_count)} cell${group.cell_count === 1 ? "" : "s"}`;
}

function missingMessage(group: BatchValidationGroup): string {
  if (group.cause === "required_missing") {
    return `${group.key}: required in ${cells(group)}`;
  }
  return `${group.key}: missing in ${cells(group)}`;
}

function valueLayerMessage(group: BatchValidationGroup): string {
  if ((group.supplied_by ?? []).length === 1) {
    return `The value came from ${supplierLayers(group)}.`;
  }
  return `The value is declared at ${declarationLayers(group)}.`;
}

function assertNever(value: never): never {
  throw new Error(`Unexpected launch diagnosis cause: ${String(value)}`);
}

export function launchDiagnosis(group: BatchValidationGroup): string[] {
  if (group.key === "unavailable_variable") {
    return [
      "One or more scenario tokens point at a variable that no longer exists.",
      `Fix the broken token at the Scenario layer. Missing in ${cells(group)}.`,
    ];
  }

  if ((group.declarations ?? []).length === 0) {
    return [
      `${group.key}: undeclared at the Agent, Persona, and Scenario layers.`,
      "It is unfillable by every persona because undeclared values are " +
        "dropped before the cascade runs.",
      missingMessage(group),
    ];
  }

  if ((group.supplied_by ?? []).length === 0) {
    // OPEN(persona-scenario-spec-keys-that-are-declarable-but-silently-skipped):
    // excluded spec keys are indistinguishable from a genuine contract miss.
    return [
      `${group.key}: declared at ${declarationLayers(group)}, but no layer ` +
        "supplied this declared slot.",
      missingMessage(group),
    ];
  }

  const cause = group.cause;
  switch (cause) {
    case "missing":
      return [missingMessage(group)];
    case "required_missing":
      return [
        missingMessage(group),
        `An explicit null supplied by ${supplierLayers(group)} left the ` +
          "required value empty.",
      ];
    case "type_error": {
      const expected =
        (group.expected_types ?? []).join(" or ") || "the declared type";
      const actual = (group.actual_types ?? []).join(" or ") || "another type";
      return [
        `${group.key}: expected ${expected}, received ${actual} in ${cells(group)}`,
        valueLayerMessage(group),
      ];
    }
    case "non_scalar": {
      const actualTypes = group.actual_types ?? [];
      const failure =
        actualTypes.length === 1 && actualTypes[0] === "null"
          ? `${group.key}: received an empty value in ${cells(group)}`
          : `${group.key}: expected a scalar, received ${
              actualTypes.join(" or ") || "a non-scalar value"
            } in ${cells(group)}`;
      return [failure, valueLayerMessage(group)];
    }
    case "not_allowed":
      return [
        `${group.key}: value is not allowed in ${cells(group)}`,
        valueLayerMessage(group),
      ];
    default:
      return assertNever(cause);
  }
}

export function validationExampleMessage(
  group: Pick<BatchValidationGroup, "examples"> | BatchWarningGroup,
): string {
  return group.examples
    .map((example) =>
      example.scenario_row_id === null
        ? `cell ${example.cell_key}`
        : `row ${example.scenario_row_id} (cell ${example.cell_key})`,
    )
    .join(", ");
}

export function warningGroupMessage(group: BatchWarningGroup): string {
  const cause = group.cause;
  switch (cause) {
    // Generated as a one-member union today; the switch guards future causes.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    case "scenario_row_undeclared_keys": {
      const keys = formatList(group.keys ?? []);
      const namedKeys = keys ? ` ${keys}` : "";
      return `The Scenario row layer ignored undeclared keys${namedKeys} in ${String(
        group.cell_count,
      )} cell${group.cell_count === 1 ? "" : "s"}.`;
    }
    default:
      return assertNever(cause);
  }
}

export function orderValidationGroups(
  groups: BatchValidationGroup[],
): BatchValidationGroup[] {
  // ASSUMED(receipt sorts validation groups by planner cause precedence;
  // server order is alphabetical).
  return [...groups].sort((left, right) => {
    const causeOrder =
      CAUSE_PRECEDENCE[left.cause] - CAUSE_PRECEDENCE[right.cause];
    return causeOrder || left.key.localeCompare(right.key);
  });
}
